// Track Changes Service
// Handles enabling Track Changes in Word documents for review workflows

/**
 * Enables Track Changes in a DOCX file by modifying the OOXML structure
 * This allows all edits to be tracked with reviewer names and timestamps
 */
export const enableTrackChangesInDocx = async (docxBuffer) => {
  try {
    // DOCX files are ZIP archives
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();

    // Load the DOCX file
    const loaded = await zip.loadAsync(docxBuffer);

    // Get document.xml
    const docFile = loaded.file('word/document.xml');
    if (!docFile) {
      throw new Error('document.xml not found in DOCX');
    }

    let docXml = await docFile.async('string');

    // Add Track Changes to document.xml
    // w:trackRevisions must be added at the document root level, after w:documentPr if it exists
    if (!docXml.includes('w:trackRevisions')) {
      // Try to add after w:documentPr if it exists
      if (docXml.includes('</w:documentPr>')) {
        docXml = docXml.replace(
          '</w:documentPr>',
          '</w:documentPr><w:trackRevisions/>'
        );
      } else {
        // Otherwise add right before w:body
        docXml = docXml.replace(
          /<w:body/,
          '<w:trackRevisions/><w:body'
        );
      }
      console.log('[LexCore] Added w:trackRevisions to document.xml');
    }

    // Update document.xml in the ZIP
    zip.file('word/document.xml', docXml);

    // Update settings.xml to enable Track Changes at application level
    const settingsFile = loaded.file('word/settings.xml');
    if (settingsFile) {
      let settingsXml = await settingsFile.async('string');

      // Add w:trackRevisions element to settings if not present
      if (!settingsXml.includes('w:trackRevisions')) {
        settingsXml = settingsXml.replace(
          /<\/w:settings>/,
          '<w:trackRevisions/></w:settings>'
        );
        zip.file('word/settings.xml', settingsXml);
        console.log('[LexCore] Added w:trackRevisions to settings.xml');
      }
    }

    // Generate modified DOCX buffer
    const modifiedBuffer = await zip.generateAsync({
      type: 'arraybuffer',
      compression: 'DEFLATE',
      compressionOptions: { level: 9 }
    });

    console.log('[LexCore] Track Changes modification complete. Document size: ' + (modifiedBuffer.byteLength / 1024).toFixed(1) + 'KB');
    return modifiedBuffer;
  } catch (error) {
    console.error('[LexCore] Error enabling Track Changes:', error);
    console.error('[LexCore] Returning original document without Track Changes');
    // Return original buffer if modification fails
    return docxBuffer;
  }
};

/**
 * Creates a notification message to include in emails about Track Changes
 */
export const getTrackChangesEmailNotice = () => {
  return `
    <div style="background:#fff3cd;border-left:4px solid #ca5010;padding:12px 16px;margin:16px 0;border-radius:4px;">
      <div style="font-weight:600;color:#ca5010;margin-bottom:6px;">📝 Track Changes Enabled</div>
      <div style="color:#333;font-size:13px;">
        This document has <strong>Track Changes enabled</strong>. All your edits will be recorded with your name and timestamp.
        You can see all changes in Word by going to <strong>Review → Track Changes → Show Markup</strong>.
      </div>
    </div>
  `;
};

/**
 * Get instructions for reviewers on using Track Changes
 */
export const getTrackChangesInstructions = () => {
  return `
<div style="background:#f0f0f0;border-left:4px solid #0078d4;padding:16px;margin:16px 0;border-radius:4px;">
  <h3 style="color:#0078d4;margin-top:0;">How to Review with Track Changes</h3>
  <ol style="color:#333;font-size:14px;line-height:1.6;">
    <li><strong>Open the document</strong> in Microsoft Word</li>
    <li>Make any edits or additions you need</li>
    <li>Your changes will be <span style="color:#ca5010;font-weight:600;">automatically tracked</span> with:
      <ul>
        <li>Your name</li>
        <li>The date and time of change</li>
        <li>What changed (highlighted in the document)</li>
      </ul>
    </li>
    <li>When done, save the document</li>
    <li>The contract owner will review your changes and accept/reject them</li>
  </ol>
  <p style="color:#666;font-size:13px;margin-bottom:0;">
    <strong>Tip:</strong> To see all changes, go to <strong>Review → Track Changes → Show Markup</strong>
  </p>
</div>
  `;
};
